export interface Capability {
    id: string;
    app_key: string;
    name: string;
    /** Human-readable title when set; UI should fall back to `name`. */
    display_name?: string | null;
    fqcn: string;
    type: string;
    description: string | null;
    aliases: string[];
    tags: string[];
    status: string;
    visibility: string;
    domain: string | null;
    content_hash: string | null;
    file_ref: string | null;
    is_system: boolean;
    object: string;
    revision: number;
    created: string;
    created_by: string;
    modified: string;
    modified_by: string;
    config: Record<string, unknown>;
    ext: Record<string, unknown>;
    working_version?: string;
    latest_version?: string;
    method_count?: number;
}

export interface CapabilityRevision {
    id: string;
    capability_id: string;
    revision_kind: 'working' | 'immutable';
    major: number;
    minor: number;
    patch: number;
    prerelease: string | null;
    version_string: string;
    status: 'active' | 'deprecated' | 'disabled';
    deprecation_reason: string | null;
    deprecated_at: string | null;
    type_config: Record<string, unknown>;
    content_hash: string | null;
    description: string | null;
    changelog: string | null;
    created: string;
    created_by: string;
    modified: string;
    modified_by: string;
    metadata: Record<string, unknown>;
    config: Record<string, unknown>;
    /** ID of the immutable revision this working copy was based on. */
    base_revision_id?: string | null;
    methods?: CapabilityMethod[];
    files?: CapabilityRevisionFile[];
}

export interface CapabilityMethod {
    id: string;
    revision_id: string;
    name: string;
    description: string | null;
    semantics: string | null;
    input_schema: Record<string, unknown>;
    output_schema: Record<string, unknown>;
    input_schema_ref?: string | null;
    output_schema_ref?: string | null;
    tags: string[];
    policies: Record<string, unknown>;
    examples: unknown[];
    channels: string[];
}

export interface CapabilityRevisionFile {
    id: string;
    revision_id: string;
    path: string;
    content_type: string;
    content_text: string | null;
    size_bytes: number;
    sha256: string;
}

export interface CapabilityDefault {
    id: string;
    capability_id: string;
    default_major: number;
    env: string;
}

export interface Application {
    id: string;
    app_key: string;
    name: string | null;
    status: string;
    config: Record<string, unknown>;
    organization_id?: string;
    created: string;
    created_by: string;
    modified: string;
    modified_by: string;
    capability_count?: number;
}

export type CapabilityVisibility = 'global' | 'organization' | 'application' | 'account' | 'user';

export interface ApplicationCapabilityVisibilityRow {
    capability_id: string;
    capability_name: string;
    capability_display_name: string | null;
    capability_type: string;
    capability_status: string;
    capability_visibility: CapabilityVisibility;
    app_visibility_override: CapabilityVisibility | null;
    effective_visibility: CapabilityVisibility;
}

export const CAPABILITY_TYPES = [
    'workflow', 'agent', 'model', 'prompt', 'tool', 'skill', 'memory',
    'role', 'policy', 'permission', 'decision', 'schema', 'subscription', 'domain-type',
    'application', 'provider', 'worker', 'sandbox', 'workspace', 'integration', 'connection',
] as const;

export type CapabilityType = (typeof CAPABILITY_TYPES)[number];

export const CAPABILITY_TYPE_LABELS: Record<string, string> = {
    'workflow': 'Workflow',
    'agent': 'Agent',
    'model': 'Model',
    'prompt': 'Prompt',
    'tool': 'Tool',
    'skill': 'Skill',
    'role': 'Role',
    'memory': 'Memory',
    'policy': 'Policy',
    'permission': 'Permission',
    'decision': 'Decision',
    'schema': 'Schema',
    'subscription': 'Subscription',
    'domain-type': 'Domain Type',
    'application': 'Application',
    'provider': 'Provider',
    'worker': 'Worker',
    'sandbox': 'Sandbox',
    'workspace': 'Workspace',
    'binding': 'Binding',
    'integration': 'Integration',
    'connection': 'Connection',
};

/** URL for capability detail; type is not in the path (API is type-agnostic). */
export function capabilityDetailPath(appKey: string, _type: string, name: string): string {
    return `/applications/${appKey}/capabilities/${name}`;
}

/** Primary label for lists and headers: display name when set, else stable `name`. */
export function capabilityDisplayTitle(
    item: Pick<Capability, 'name'> & { display_name?: string | null },
): string {
    const d = item.display_name?.trim();
    return d || item.name;
}
